import { useId, useState, type FormEvent } from 'react'
import { Link } from 'react-router'
import { Button } from '../../ui'
import { removeAiSettings, saveAiSettings, testAi, type AiSettings } from '../../lib/api'
import { formatDate } from './LiveFindings'
import { describeFailure, type Load } from './useApi'
import { SettingsSection } from './SettingsSection'
import './live.css'

/**
 * Settings > AI: the workspace's own Azure OpenAI (docs/byo-ai-design.md, slice 1).
 *
 * Shown only where bring-your-own AI is switched on (the server answers 404 otherwise). Owner decisions, 2026-10-10:
 * the customer's enterprise Azure OpenAI only, the owner alone configures it, Monitor only, redacted evidence by
 * default, a daily limit the customer sets.
 *
 * KEYLESS. There is no key field because there is no key: the customer gives Defenssive's application the Cognitive
 * Services OpenAI User role on their own resource, as they give Reader for Azure assessment. The steps say so.
 *
 * Nothing on this page sends a finding anywhere. The Test sends one fixed sentence; findings are sent only from a
 * finding's page, when a person asks (slice 2).
 */
export function AiSettingsCard({
  ai,
  isOwner,
  onChanged,
}: {
  ai: Load<AiSettings>
  isOwner: boolean
  onChanged: () => void
}) {
  if (ai.state === 'loading') return <div className="live-empty">Loading…</div>
  if (ai.state === 'error') return <div className="live-empty">{describeFailure(ai.status)}</div>

  const a = ai.data
  const reviewAvailable = a.onMonitor === true && a.recordedEvidenceReviewAvailable === true
  if (!a.onMonitor) {
    return (
      <SettingsSection title="AI">
        <p className="settings-note" style={{ marginTop: 0 }}>
          Using your own Azure OpenAI to explain findings is part of the Monitor plan.
        </p>
        <p className="settings-note">
          <Link to="/plans">See plans</Link>
        </p>
      </SettingsSection>
    )
  }

  return (
    <>
      <SettingsSection
        title="Your Azure OpenAI"
        description={
          <p>
            Connect your organisation&rsquo;s Azure OpenAI for on-demand explanations and fix plans.{' '}
            {reviewAvailable ? <>In an approved pilot, only the workspace owner can request a recorded-evidence
              review. Connecting AI does not enable that pilot.{' '}</> : null}
            Saving these settings sends no finding. Test sends a fixed test message.
          </p>
        }
      >
        {a.settings ? <Saved settings={a.settings} reviewAvailable={reviewAvailable} /> : (
          <p className="settings-note" style={{ marginTop: 0 }}>AI is off: no Azure OpenAI resource is set.</p>
        )}
        {isOwner ? <OwnerControls key={`${a.workspaceId}:${reviewAvailable}`}
          ai={a} reviewAvailable={reviewAvailable} onChanged={onChanged} /> : (
          <p className="settings-note">Only the workspace owner can change these settings.</p>
        )}
      </SettingsSection>

      <SettingsSection
        title="Give Defenssive access to your Azure OpenAI"
        description={
          <p>
            That role lets Defenssive send requests to your deployed models and read the resource. It cannot
            change or create anything. There is no key to copy: Defenssive signs in as its own application.
            A new assignment can take a few minutes to work.
          </p>
        }
      >
        <ol className="settings-steps">
          <li>
            In the Azure portal, <span className="mono">portal.azure.com</span>, open your{' '}
            <strong>Azure OpenAI</strong> resource.
          </li>
          <li>
            Open <strong>Access control (IAM)</strong>, then <strong>Add</strong> &gt;{' '}
            <strong>Add role assignment</strong>.
          </li>
          <li>
            Select <strong>Cognitive Services OpenAI User</strong>, then <strong>Next</strong>.
          </li>
          <li>
            Choose <strong>Select members</strong>, search for <strong>Defenssive</strong>, and add the
            application, not a person. Then choose <strong>Review + assign</strong>.
          </li>
        </ol>
      </SettingsSection>
    </>
  )
}

function Saved({ settings: s, reviewAvailable }: {
  settings: NonNullable<AiSettings['settings']>; reviewAvailable: boolean
}) {
  return (
    <dl className="settings-grid">
      <dt>Resource</dt>
      <dd className="mono">{s.azureResource}.openai.azure.com</dd>
      <dt>Deployment</dt>
      <dd className="mono">{s.azureDeployment}</dd>
      <dt>Daily limit</dt>
      <dd>{s.dailyCap} requests a day</dd>
      <dt>Evidence sharing</dt>
      <dd>{s.evidenceLevel === 'none'
        ? 'None: Explain and Fix plan send finding context only.' + (reviewAvailable ? ' Review makes no model call.' : '')
        : 'Filtered: Explain and Fix plan replace recognised names and identifiers; names inside other free text may remain.'
          + (reviewAvailable ? ' Review permits only recognised structured evidence values.' : '')}</dd>
      <dt>Last test</dt>
      <dd>{s.lastTestedAt ? `${formatDate(s.lastTestedAt)}: ${describeTest(s.lastTestOutcome, s.lastTestModel)}` : 'Not tested yet.'}</dd>
      <dt>Turned on by</dt>
      <dd>{s.acknowledgedBy ?? 'A former member'}, {formatDate(s.acknowledgedAt)}</dd>
    </dl>
  )
}

/** A Test's outcome, in words (0195's closed list). */
export function describeTest(outcome: string | null, model: string | null): string {
  switch (outcome) {
    case 'ok':
      return model ? `Azure OpenAI answered, using ${model}.` : 'Azure OpenAI answered.'
    case 'forbidden':
      return 'Azure refused: give Defenssive the Cognitive Services OpenAI User role on this resource (steps below).'
    case 'not_found':
      return 'Azure found no such deployment on this resource. Check the deployment name.'
    case 'throttled':
      return 'Azure said the deployment is busy or out of quota. Try again later.'
    case 'unreachable':
      return 'No Azure OpenAI resource answered at that name. Check the resource name.'
    case 'no_token':
      return 'Defenssive could not sign in to your tenant. Check that Defenssive is still connected.'
    default:
      return 'Azure OpenAI gave an answer Defenssive did not expect.'
  }
}

function OwnerControls({ ai, reviewAvailable, onChanged }: {
  ai: AiSettings; reviewAvailable: boolean; onChanged: () => void
}) {
  const ids = { resource: useId(), deployment: useId(), cap: useId(), evidence: useId(), ack: useId() }
  const s = ai.settings
  const [resource, setResource] = useState(s?.azureResource ?? '')
  const [deployment, setDeployment] = useState(s?.azureDeployment ?? '')
  const [cap, setCap] = useState(String(s?.dailyCap ?? ai.limits.defaultCap))
  const [evidence, setEvidence] = useState(s?.evidenceLevel === 'none' ? 'none' : 'redacted')
  const [acknowledged, setAcknowledged] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // Once AI is set, the summary above already shows every value, so the form opens on request.
  const [editing, setEditing] = useState(false)

  async function run(action: () => ReturnType<typeof testAi>, after?: () => void) {
    setBusy(true)
    setError(null)
    const result = await action()
    setBusy(false)
    if (!result.ok) setError(result.message)
    else after?.()
    onChanged()
  }

  const errorLine = error ? <p className="live-error" role="alert" style={{ marginTop: 'var(--space-3)' }}>{error}</p> : null

  if (s && !editing) {
    return (
      <>
        {errorLine}
        <div className="settings-actions">
          <Button type="button" variant="primary" disabled={busy} onClick={() => setEditing(true)}>Change settings</Button>
          <Button type="button" variant="primary" disabled={busy} onClick={() => void run(testAi)}>Test</Button>
          <Button type="button" variant="ghost" disabled={busy} onClick={() => void run(removeAiSettings)}>Turn off AI</Button>
        </div>
      </>
    )
  }

  function save(event: FormEvent) {
    event.preventDefault()
    void run(() => saveAiSettings({
      azureResource: resource.trim().toLowerCase(),
      azureDeployment: deployment.trim(),
      dailyCap: Number(cap),
      evidenceLevel: evidence,
      acknowledged,
    }), () => {
      setEditing(false)
      setAcknowledged(false)
    })
  }

  return (
    <form className="invite-form settings-form-grid" onSubmit={save} style={{ marginTop: s ? 'var(--space-4)' : 0 }}>
      <label className="invite-field" htmlFor={ids.resource}>
        <span className="invite-label">Azure OpenAI resource name</span>
        <input id={ids.resource} className="findings-search-input" value={resource}
          onChange={(e) => setResource(e.target.value)} placeholder="contoso-ai" autoComplete="off"
          spellCheck={false} required />
        <span className="settings-hint">The first part of its address: contoso-ai for contoso-ai.openai.azure.com.</span>
      </label>

      <label className="invite-field" htmlFor={ids.deployment}>
        <span className="invite-label">Deployment name</span>
        <input id={ids.deployment} className="findings-search-input" value={deployment}
          onChange={(e) => setDeployment(e.target.value)} placeholder="gpt-4o" autoComplete="off"
          spellCheck={false} required />
        <span className="settings-hint">
          Use the deployment name from your Azure OpenAI resource.{' '}
          {reviewAvailable
            ? 'Recorded-evidence review requires a deployment that accepts the required structured output. A successful connection Test is not a review-compatibility or accuracy test.'
            : 'A successful connection Test is not an accuracy test.'}
        </span>
      </label>

      <label className="invite-field" htmlFor={ids.cap}>
        <span className="invite-label">Daily limit</span>
        <input id={ids.cap} className="findings-search-input" type="number" min={ai.limits.minCap}
          max={ai.limits.maxCap} value={cap} onChange={(e) => setCap(e.target.value)} required />
        <span className="settings-hint">
          Requests a day across the workspace, from {ai.limits.minCap} to {ai.limits.maxCap}.{' '}
          {reviewAvailable ? 'Explain, Fix plan, Test and model-backed Review' : 'Explain, Fix plan and Test'}{' '}
          share this daily allowance. Failed model requests count too. It limits logical requests,
          not tokens or cost; retries can add usage.{' '}
          {reviewAvailable ? <>Local Insufficient evidence results do not call the model or use this allowance.{' '}</> : null}
          Azure usage is billed to your organisation; this is not a spending cap.
        </span>
      </label>

      <label className="invite-field" htmlFor={ids.evidence}>
        <span className="invite-label">Evidence sharing</span>
        <select id={ids.evidence} className="findings-search-input" value={evidence}
          onChange={(e) => setEvidence(e.target.value)}>
          <option value="redacted">{reviewAvailable
            ? 'Filtered evidence (see how each action handles it)'
            : 'Redacted: recognised names and identifiers replaced'}</option>
          <option value="none">None</option>
        </select>
        {/* Owner-approved, 2026-10-13: Explain/Fix plan and Review have different input policies. */}
        <span className="settings-hint">
          Explain and Fix plan replace recognised names and identifiers; names inside other free text may remain.
          {reviewAvailable ? <> Review admits only recognised permissions, supported setting values and counts,
            and withholds other evidence values.</> : null}{' '}
          With None, Explain and Fix plan send finding context without evidence.
          {reviewAvailable ? <> Review returns local Insufficient evidence without calling the model.</> : null}
        </span>
      </label>

      <label className="invite-role" htmlFor={ids.ack}>
        <input id={ids.ack} type="checkbox" checked={acknowledged} onChange={(e) => setAcknowledged(e.target.checked)} />
        <span>
          I authorise use of this Azure OpenAI resource under our organisation&rsquo;s Microsoft agreement for
          on-demand explanations and fix plans, with this evidence setting.{' '}
          {reviewAvailable ? <>A review pilot requires separate enablement and my acknowledgement on the finding page.{' '}</> : null}
          I understand the daily request allowance is not a spending cap.
        </span>
      </label>
      <p className="settings-hint"><a href="/privacy">What is sent and kept</a></p>

      {errorLine}

      <div className="settings-actions">
        <Button type="submit" variant="primary" disabled={busy || !acknowledged}>{s ? 'Save changes' : 'Turn on AI'}</Button>
        {s ? <Button type="button" variant="ghost" disabled={busy} onClick={() => { setEditing(false); setError(null) }}>Cancel</Button> : null}
      </div>
    </form>
  )
}
